import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  date: string;
  imageUrl: string;
  category: string;
}

const blogPosts: Record<string, BlogPost> = {
  '1': {
    id: '1',
    title: 'Comment rédiger une offre d\'emploi qui attire les meilleurs talents',
    content: `
      <h2>Les fondamentaux d'une offre d'emploi efficace</h2>
      <p>Une offre d'emploi efficace doit être claire, concise et attractive. Voici les éléments essentiels à inclure :</p>
      <ul>
        <li>Un titre de poste clair et précis</li>
        <li>Une description détaillée des responsabilités</li>
        <li>Les compétences requises et souhaitées</li>
        <li>Les avantages et bénéfices offerts</li>
      </ul>

      <h2>Structure recommandée</h2>
      <p>Pour maximiser l'impact de votre offre, suivez cette structure :</p>
      <ol>
        <li>Introduction attractive</li>
        <li>Présentation de l'entreprise</li>
        <li>Description du poste</li>
        <li>Profil recherché</li>
        <li>Ce que nous offrons</li>
        <li>Comment postuler</li>
      </ol>

      <h2>Conseils pour se démarquer</h2>
      <p>Pour rendre votre offre plus attractive :</p>
      <ul>
        <li>Utilisez un ton authentique qui reflète votre culture d'entreprise</li>
        <li>Mettez en avant vos valeurs et votre mission</li>
        <li>Soyez transparent sur le processus de recrutement</li>
        <li>Incluez des témoignages d'employés</li>
      </ul>

      <h2>L'importance des mots-clés</h2>
      <p>Les mots-clés sont essentiels pour optimiser la visibilité de votre offre :</p>
      <ul>
        <li>Utilisez des termes couramment recherchés dans votre secteur</li>
        <li>Incluez les compétences techniques spécifiques</li>
        <li>Mentionnez les certifications pertinentes</li>
        <li>Ajoutez des variations courantes des titres de poste</li>
      </ul>
    `,
    date: '15 juin 2023',
    imageUrl: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Recrutement'
  },
  '2': {
    id: '2',
    title: 'Les erreurs à éviter dans vos annonces de recrutement',
    content: `
      <h2>Les erreurs courantes</h2>
      <p>Voici les principales erreurs à éviter dans vos offres d'emploi :</p>
      <ul>
        <li>Description de poste trop vague ou imprécise</li>
        <li>Exigences irréalistes ou contradictoires</li>
        <li>Absence d'informations sur l'entreprise</li>
        <li>Manque de transparence sur le salaire</li>
      </ul>

      <h2>Impact sur le recrutement</h2>
      <p>Ces erreurs peuvent avoir des conséquences importantes :</p>
      <ul>
        <li>Candidatures inadaptées</li>
        <li>Perte de temps dans le processus de recrutement</li>
        <li>Image négative de l'entreprise</li>
        <li>Difficulté à attirer les meilleurs talents</li>
      </ul>

      <h2>Solutions et bonnes pratiques</h2>
      <p>Pour éviter ces pièges, suivez ces recommandations :</p>
      <ol>
        <li>Soyez précis dans vos descriptions</li>
        <li>Restez réaliste dans vos exigences</li>
        <li>Présentez clairement votre entreprise</li>
        <li>Indiquez une fourchette de salaire</li>
      </ol>
    `,
    date: '28 juillet 2023',
    imageUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Conseils'
  },
  '3': {
    id: '3',
    title: 'L\'IA révolutionne le processus de recrutement',
    content: `
      <h2>L'impact de l'IA sur le recrutement</h2>
      <p>L'intelligence artificielle transforme le processus de recrutement de plusieurs manières :</p>
      <ul>
        <li>Automatisation du tri des CV</li>
        <li>Analyse prédictive des candidatures</li>
        <li>Personnalisation des offres d'emploi</li>
        <li>Optimisation du matching candidat-poste</li>
      </ul>

      <h2>Avantages pour les recruteurs</h2>
      <p>L'utilisation de l'IA présente de nombreux avantages :</p>
      <ul>
        <li>Gain de temps significatif</li>
        <li>Réduction des biais de recrutement</li>
        <li>Amélioration de la qualité des embauches</li>
        <li>Meilleure expérience candidat</li>
      </ul>

      <h2>Perspectives d'avenir</h2>
      <p>Les développements futurs de l'IA dans le recrutement :</p>
      <ul>
        <li>Entretiens vidéo automatisés</li>
        <li>Évaluation des soft skills par l'IA</li>
        <li>Prédiction de la performance future</li>
        <li>Personnalisation poussée du processus</li>
      </ul>
    `,
    date: '10 septembre 2023',
    imageUrl: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Technologie'
  }
};

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const post = id ? blogPosts[id] : null;

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Article non trouvé</h1>
          <Link to="/" className="btn-primary">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Link to="/" className="link-gradient inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>Retour aux articles</span>
          </Link>
        </div>

        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-64 md:h-96 relative">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="inline-block bg-gradient-primary text-white text-sm px-3 py-1 rounded-full mb-4">
                {post.category}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {post.title}
              </h1>
              <div className="flex items-center text-white/80">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{post.date}</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;